import * as React from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { Block } from "../library/container"
import { nl } from "./datepickerUtils"
import { submitForm } from "./newsletterFormBlockApi"
import {
  allFields,
  Branch,
  branches,
  Field,
  genderLabels,
  genderValues,
  initialState,
  Newsletter,
  NewsletterFormBlockProps,
  NewsletterFormBlockState,
  newsletterLabels,
  newsletters,
  setFieldOnState,
  setVisited,
  toggleNewsletterOnState,
  validateFields,
  validateFormData,
} from "./newsletterFormBlockState"
import debounce = require("lodash.debounce")
import { Loader } from "../loader"
import { Link } from "../library/link"
import { slugify } from "../../../custom_utils/helpers"

registerLocale("nl", nl)

export class NewsletterFormBlock extends React.Component<NewsletterFormBlockProps, NewsletterFormBlockState> {
  constructor(props: NewsletterFormBlockProps) {
    super(props)

    this.state = initialState
  }

  validate = validateFormData(this.setState.bind(this))
  validateVisitedAfterDelay = debounce(() => this.validate(), 1000)

  submit() {
    const validationMessages = validateFields(allFields, this.state.data)
    if (validationMessages.count() > 0) {
      this.setState(prevState => ({ ...prevState, validationMessages }))
      const offsetForm = document.getElementById("newsletter-form").offsetTop;
      window.scrollTo({ top: offsetForm - 100, behavior: 'smooth'});
      return
    }

    this.setState(
      prevState => ({ ...prevState, status: "submitting" }),
      async () => {
        const status = (await submitForm(this.state.data)) ? "succeeded" : "failed"
        this.setState(prevState => ({ ...prevState, status }))
      }
    )
  }

  hasError(field: Field): boolean {
    const messages = this.state.validationMessages
    return messages.has(field) ? true : false
  }

  maybeShowMessage(field: Field): JSX.Element {
    const messages = this.state.validationMessages
    return messages.has(field) ? (
      <p id={`nfd-${field}`} className="form__description form__error">
        {messages.get(field)}
      </p>
    ) : (
      <></>
    )
  }

  render() {
    if (this.state.status == "submitting") return submitting
    if (this.state.status == "succeeded") return succeeded
    if (this.state.status == "failed") return failed

    const setField = setFieldOnState(this.setState.bind(this))
    const set = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setField(field)(e.target.value)
      this.validateVisitedAfterDelay()
    }

    const toggler = toggleNewsletterOnState(this.setState.bind(this))
    const toggleNewsletter = (newsletter: Newsletter) => {
      toggler(newsletter)
      this.validateVisitedAfterDelay()
    }

    const setVisit = setVisited(this.setState.bind(this))
    const visit = (field: Field) => () => setVisit(field)

    const {
      firstName,
      initials,
      infix,
      lastName,
      emailAddress,
      dateOfBirth,
      gender,
      branch,
      newsletters: selectedNewsletters,
    } = this.state.data
    const canSubmit = this.state.validationMessages.size == 0

    return (
      <Block>
        <div id="newsletter-form" className="newsletter-form">
          <fieldset>
            <legend className="form__title title--ms">Persoonlijke informatie</legend>
            <div className="form__group form__group--name">
              <div className="form__field form__field--long">
                <label className="form__label" htmlFor="firstName">
                  Voornaam
                </label>
                <input
                  id="firstName"
                  className="form__input"
                  name="firstName"
                  type="text"
                  onFocus={visit("firstName")}
                  onChange={set("firstName")}
                  value={firstName}
                  autoComplete="given-name"
                  aria-invalid={this.hasError("firstName") ? true : undefined}
                  aria-errormessage={this.hasError("firstName") ? `nfd-firstName` : undefined}
                />
                {this.maybeShowMessage("firstName")}
              </div>
              <div className="form__field form__field--short">
                <label className="form__label form__label--required" htmlFor="initials">
                  Voorletters
                </label>
                <input
                  id="initials"
                  className="form__input"
                  name="initials"
                  type="text"
                  onFocus={visit("initials")}
                  onChange={set("initials")}
                  value={initials}
                  aria-invalid={this.hasError("initials") ? true : undefined}
                  aria-errormessage={this.hasError("initials") ? `nfd-initials` : undefined}
                  aria-required={true}
                  autoComplete="on"
                />
                {this.maybeShowMessage("initials")}
              </div>
              <div className="form__field name form__field--short">
                <label className="form__label" htmlFor="infix">
                  Tussenvoegsel
                </label>
                <input
                  id="infix"
                  className="form__input"
                  name="infix"
                  type="text"
                  onFocus={visit("infix")}
                  onChange={set("infix")}
                  autoComplete="on"
                  value={infix}
                />
              </div>
              <div className="form__field form__field--long">
                <label className="form__label form__label--required" htmlFor="lastName">
                  Achternaam
                </label>
                <input
                  id="lastName"
                  className="form__input"
                  name="lastName"
                  type="text"
                  onFocus={visit("lastName")}
                  onChange={set("lastName")}
                  value={lastName}
                  autoComplete="family-name"
                  aria-invalid={this.hasError("lastName") ? true : undefined}
                  aria-errormessage={this.hasError("lastName") ? `nfd-lastName` : undefined}
                  aria-required={true}
                />
                {this.maybeShowMessage("lastName")}
              </div>
            </div>
            <div className="form__field">
              <label className="form__label form__label--required" htmlFor="emailAddress">
                E-mailadres
              </label>
              <input
                id="emailAddress"
                className="form__input"
                name="emailAddress"
                type="email"
                onFocus={visit("emailAddress")}
                onChange={set("emailAddress")}
                value={emailAddress}
                autoComplete="email"
                aria-invalid={this.hasError("emailAddress") ? true : undefined}
                aria-errormessage={this.hasError("emailAddress") ? `nfd-emailAddress` : undefined}
                aria-required={true}
              />
              {this.maybeShowMessage("emailAddress")}
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="dateOfBirth">
                Geboortedatum
              </label>
              <div className="form__date">
                <DatePicker
                  id="dateOfBirth"
                  locale="nl"
                  selected={dateOfBirth}
                  onChange={e => {
                    setField("dateOfBirth")(e || new Date())
                  }}
                  dateFormat="d MMMM, yyyy"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  autoComplete="bday"
                />
                <svg width="15" height="17" aria-hidden="true">
                  <use xlinkHref="#icon--calendar" />
                </svg>
              </div>
            </div>
            <div className="form__field">
              <label className="form__label">Geslacht</label>
              {genderValues.map(g => (
                <div className="form__radio" key={g}>
                  <input
                    id={g}
                    type="radio"
                    name="gender"
                    value={g}
                    checked={gender == g}
                    onChange={() => setField("gender")(g)}
                  />
                  <label htmlFor={g}>
                    <span className="sr-text">Geslacht </span>
                    {genderLabels[g]}
                  </label>
                </div>
              ))}
            </div>
            <div className="form__field">
              <label className="form__label" htmlFor="branch">
                Hoofdvestiging
              </label>
              <div className="form__select">
                <select
                  id="branch"
                  name="branch"
                  value={branch}
                  onFocus={visit("branch")}
                  onChange={e => setField("branch")(e.target.value as Branch)}
                >
                  {branches.map(branch => (
                    <option key={branch} label={branch} value={branch}>
                      {branch}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form__field">
              <label className="form__label">
                Ik wil me inschrijven voor de
                <span className="form__label--required"></span>
                {newsletters.map(newsletter => (
                  <div className="form__check" key={newsletter}>
                    <input
                      id={slugify(newsletter)}
                      type="checkbox"
                      name={newsletter}
                      value={newsletter}
                      checked={selectedNewsletters.includes(newsletter)}
                      onChange={() => toggleNewsletter(newsletter)}
                      aria-invalid={this.hasError("newsletters") ? true : undefined}
                      aria-errormessage={this.hasError("newsletters") ? `nfd-newsletters` : undefined}
                      aria-describedby={`nfd-${slugify(newsletter)}`}
                    />
                    <label htmlFor={slugify(newsletter)}>{newsletter}</label>
                    <p id={`nfd-${slugify(newsletter)}`} className="form__description">
                      {newsletterLabels[newsletter]}
                    </p>
                  </div>
                ))}
                {this.maybeShowMessage("newsletters")}
              </label>
            </div>
          </fieldset>

          <p className="form__label--required--legend">* Vereist</p>
          
          <button
            className="button"
            // disabled={!canSubmit}
            onMouseOver={() => this.validate(true)}
            onClick={() => this.submit()}
          >
            Verstuur
          </button>
        </div>
      </Block>
    )
  }
}

const submitting = (
  <Block>
    <div className="newsletter-form newsletter-form--submitting">
      <h2 className="form__title title--ms">Bezig met versturen</h2>
      <Loader />
    </div>
  </Block>
)

const succeeded = (
  <Block>
    <div className="newsletter-form newsletter-form--succeeded">
      <h2 className="form__title title--ms">Inschrijving verstuurd</h2>
      <div className="text--styled">
        <p>
          Bedankt voor het aanmelden voor de nieuwsbrief! We hebben je gegevens verwerkt zodat je de nieuwsbrief
          binnenkort ontvangt.
        </p>
      </div>
    </div>
  </Block>
)

const failed = (
  <Block>
    <div className="newsletter-form newsletter-form--failed">
      <h2 className="form__title title--ms">Het inschrijven is mislukt</h2>
      <div className="text--styled">
        <p>
          Er is iets misgegaan tijdens het aanmelden voor de nieuwsbrief. Herlaadt deze pagina en probeer het nog eens.
        </p>
        <p>
          Lukt het nog steeds niet? Neem dan <Link url="/klantenservice">contact</Link> met ons op.
        </p>
      </div>
    </div>
  </Block>
)
